
import { Slider } from "@/components/ui/Slider";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useSoundtrackStore } from "../stores/soundtrackStore";
import { useIsMobile } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";

/**
 * Compact soundtrack controls with play/pause, mute, and volume slider.
 * Designed to be placed inline in the top toolbar next to the space name.
 * Per-track toggling is intentionally omitted for a clean, mobile-friendly UI.
 */
export function SoundtrackControls() {
  const soundtracks = useSoundtrackStore((s) => s.soundtracks);
  const isPlaying = useSoundtrackStore((s) => s.isPlaying);
  const volume = useSoundtrackStore((s) => s.volume);
  const isMuted = useSoundtrackStore((s) => s.isMuted);
  const isMobile = useIsMobile();

  if (soundtracks.length === 0) return null;

  const togglePlay = () => useSoundtrackStore.getState().toggleSoundtrackPlay();
  const setVolume = (v: number) =>
    useSoundtrackStore.getState().setSoundtrackVolume(v);
  const toggleMute = () => useSoundtrackStore.getState().toggleSoundtrackMute();

  return (
    <div className="flex items-center gap-1 md:gap-1.5 bg-white/90 backdrop-blur-sm border border-sands-200 rounded-lg px-1.5 md:px-2 py-1 md:py-1.5 shadow-sm">
      {/* Play / Pause */}
      <button
        onClick={togglePlay}
        className={cn(
          "min-w-[36px] min-h-[36px] md:min-w-[24px] md:min-h-[24px] flex items-center justify-center rounded-full transition-colors shrink-0",
          isPlaying
            ? "bg-healing-500 text-white hover:bg-healing-600"
            : "bg-sands-100 text-sands-600 hover:bg-sands-200",
        )}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <Pause className="w-3.5 h-3.5 md:w-3 md:h-3" />
        ) : (
          <Play className="w-3.5 h-3.5 md:w-3 md:h-3 ml-0.5" />
        )}
      </button>

      {/* Master volume */}
      <button
        onClick={toggleMute}
        className="shrink-0 min-w-[36px] min-h-[36px] md:min-w-0 md:min-h-0 flex items-center justify-center text-sands-400 hover:text-sands-600 transition-colors"
        title={isMuted ? "Unmute" : "Mute"}
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4 md:w-3.5 md:h-3.5" />
        ) : (
          <Volume2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
        )}
      </button>
      <Slider
        min={0}
        max={1}
        step={0.01}
        value={isMuted ? 0 : volume}
        onChange={(e) => setVolume(parseFloat(e.target.value))}
        size={isMobile ? "wide" : "compact"}
        title={`Volume: ${Math.round((isMuted ? 0 : volume) * 100)}%`}
      />
    </div>
  );
}
